import { proxy, snapshot, subscribe, useSnapshot } from 'valtio'
import { devtools } from 'valtio/utils'

import { assert } from '@/utils/assert'
import safeLocalStorage from '@/utils/local-storage'

import { COLUMN_DEFAULT_WIDTH } from '~shared/rundown/column'
import type { Column } from '~shared/rundown/column'
import { fastDeepEqual } from '~shared/utils'

import { rundownPageStore } from '../rundown-page.store'

import type { Snapshot } from 'valtio'

// =============== TYPES

/**
 * One user's local layout overrides for one rundown.
 */
export type ColumnLayout = {
  /** Column id to width in px, overriding the column document's width. */
  columnWidths: Record<string, number>

  /** Column ids in the user's order. Empty means the rundown's own order. */
  columnOrder: string[]

  /** Column ids the user hid for themselves. */
  hiddenColumns: string[]
}

/** Layouts keyed by rundown id. This is the persisted shape. */
type ColumnLayoutMap = Record<string, ColumnLayout>

// =============== STORE

/**
 * Persistence key.
 *
 * @warning Changing the key or the shape of `ColumnLayoutMap`,
 * breaks any layout users have already saved.
 */
const STORAGE_KEY = 'rundown-column-layout'

/**
 * The user's local column layout, per rundown, persisted to localStorage.
 *
 * An entry only exists once the user changes the layout of that rundown. There
 * is no seeding: readers fall back to the rundown's own order and widths.
 *
 * @remarks
 *   Loaded synchronously when the module is first imported, so the first render
 *   already has the saved layout. Every change is written back, and changes
 *   made in another tab are applied here through the `storage` event.
 *
 * @privateRemarks
 *   Not `valtio-persist`: its always async, top-level await is not available
 *   for our build targets). Also has no cross-tab sync and no guard against
 *   denied access.
 */
const columnLayoutStore = proxy<ColumnLayoutMap>(readStored())

/**
 * Parse the persisted map. Anything unreadable counts as no saved layouts, so
 * a corrupt entry never breaks the page.
 */
function parseStored(raw: string | null | undefined): ColumnLayoutMap {
  if (!raw) return {}

  try {
    const parsed: unknown = JSON.parse(raw)
    const isObject =
      typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)

    return isObject ? (parsed as ColumnLayoutMap) : {}
  } catch {
    return {}
  }
}

function readStored(): ColumnLayoutMap {
  return parseStored(safeLocalStorage.getItem(STORAGE_KEY))
}

/**
 * Write the store to localStorage.
 *
 * Skips the write when nothing changed, so applying another tab's change does
 * not echo it back as a new `storage` event. Compares parsed values, not
 * strings, as key order can differ between tabs.
 */
function writeStored() {
  const current = snapshot(columnLayoutStore)
  if (fastDeepEqual(readStored(), current)) return

  safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(current))
}

/**
 * Bring the store in line with a map written by another tab.
 *
 * Updates in place, entry by entry, so the proxy identity stays stable and only
 * the rundowns that actually changed notify their subscribers.
 */
function applyStored(next: ColumnLayoutMap) {
  const current = snapshot(columnLayoutStore)

  for (const rundownId of Object.keys(current)) {
    if (!(rundownId in next)) delete columnLayoutStore[rundownId]
  }

  for (const [rundownId, layout] of Object.entries(next)) {
    if (fastDeepEqual(current[rundownId], layout)) continue

    columnLayoutStore[rundownId] = layout
  }
}

subscribe(columnLayoutStore, writeStored)

// Reading `window.localStorage` throws when access is denied, so only listen
// when it is available.
if (safeLocalStorage.available()) {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return
    if (event.storageArea !== window.localStorage) return

    applyStored(parseStored(event.newValue))
  })
}

/**
 * Drop ids of deleted columns from the current rundown's entry.
 *
 * Runs on every change to the rundown, so a column deleted here or by another
 * user leaves no trace in storage. Writes only when something was dropped.
 */
function pruneCurrentLayout() {
  const { rundown } = rundownPageStore
  const layout = rundown?.id ? columnLayoutStore[rundown.id] : undefined
  if (!layout || !rundown.columns) return

  const pruned = pruneColumnLayout(layout, new Set(rundown.columns))
  if (fastDeepEqual(pruned, snapshot(layout))) return

  Object.assign(layout, pruned)
}

subscribe(rundownPageStore, pruneCurrentLayout)

// =============== ACTIONS

/**
 * The current rundown's entry, created on first write.
 *
 * Returns undefined outside a rundown, so the actions become no-ops.
 */
function getOrCreateLayout(): ColumnLayout | undefined {
  const rundownId = rundownPageStore.rundown?.id
  if (!rundownId) return undefined

  columnLayoutStore[rundownId] ??= {
    columnWidths: {},
    columnOrder: [],
    hiddenColumns: [],
  }

  return columnLayoutStore[rundownId]
}

/**
 * Apply `patch` to the current rundown's entry, then drop ids of columns that
 * are no longer in the rundown, so deleted columns do not pile up in storage.
 */
function patchLayout(patch: Partial<ColumnLayout>) {
  const layout = getOrCreateLayout()
  if (!layout) return

  const validIds = new Set(rundownPageStore.rundown.columns)

  Object.assign(layout, pruneColumnLayout({ ...layout, ...patch }, validIds))
}

/**
 * Every change to the user's local layout of the current rundown.
 *
 * The rundown and the current layout are read at call time, so callers only
 * hand over the column they act on.
 */
export const columnLayoutActions = {
  /** Hide a column for this user, or show it again if it is hidden. */
  toggleHidden: (columnId: string) => {
    const hiddenColumns = getOrCreateLayout()?.hiddenColumns ?? []

    patchLayout({
      hiddenColumns: hiddenColumns.includes(columnId)
        ? hiddenColumns.filter((id) => id !== columnId)
        : [...hiddenColumns, columnId],
    })
  },

  /** Show a column this user hid. */
  showColumn: (columnId: string) => {
    const hiddenColumns = getOrCreateLayout()?.hiddenColumns ?? []

    patchLayout({
      hiddenColumns: hiddenColumns.filter((id) => id !== columnId),
    })
  },

  /** Show every column this user hid. URL-hidden columns stay hidden. */
  showAll: () => {
    patchLayout({ hiddenColumns: [] })
  },

  /** Replace the user's column order. */
  setOrder: (columnOrder: readonly string[]) => {
    patchLayout({ columnOrder: [...columnOrder] })
  },

  /** Override one column's width for this user. */
  setWidth: (columnId: string, width: number) => {
    const columnWidths = getOrCreateLayout()?.columnWidths ?? {}

    patchLayout({ columnWidths: { ...columnWidths, [columnId]: width } })
  },

  /** Drop the user's layout of the current rundown entirely. */
  reset: () => {
    const rundownId = rundownPageStore.rundown?.id
    if (!rundownId) return

    delete columnLayoutStore[rundownId]
  },
}

/**
 * The store itself, for non-React readers.
 *
 * Read-only by type, so writes have to go through the actions above.
 */
export const columnLayoutStoreReadonly: Readonly<ColumnLayoutMap> =
  columnLayoutStore

// =============== HELPERS

/**
 * Copy of `layout` without ids that are not in `validIds`, in `columnOrder`,
 * `hiddenColumns` and `columnWidths`.
 */
export function pruneColumnLayout(
  layout: Snapshot<ColumnLayout>,
  validIds: ReadonlySet<string>,
): ColumnLayout {
  return {
    columnOrder: layout.columnOrder.filter((id) => validIds.has(id)),
    hiddenColumns: layout.hiddenColumns.filter((id) => validIds.has(id)),
    columnWidths: Object.fromEntries(
      Object.entries(layout.columnWidths).filter(([id]) => validIds.has(id)),
    ),
  }
}

/**
 * The user's saved order, reconciled against the rundown's order.
 *
 * - Without a saved order, the rundown's order is returned as is.
 * - Saved ids no longer in the rundown are dropped, and repeated ones kept once.
 * - A new column (in the rundown, not in the saved order) goes right after the
 *   column that precedes it in the rundown's order.
 * - A new column that comes first in the rundown goes to the start.
 *
 * @remarks
 *   Following the rundown's order is what keeps "add column after" in place for
 *   a user with a custom layout. Appending new columns at the end would not.
 */
export function reconcileColumnOrder(
  baseOrder: readonly string[],
  savedOrder: readonly string[],
): readonly string[] {
  if (!savedOrder.length) return baseOrder

  const baseSet = new Set(baseOrder)
  const kept = [...new Set(savedOrder)].filter((id) => baseSet.has(id))
  const keptSet = new Set(kept)

  // New ids, grouped under the saved id they follow in the rundown's order.
  const leading: string[] = []
  const followers = new Map<string, string[]>()
  let anchor: string | undefined

  for (const id of new Set(baseOrder)) {
    if (keptSet.has(id)) {
      anchor = id
    } else if (anchor === undefined) {
      leading.push(id)
    } else {
      const group = followers.get(anchor) ?? []
      group.push(id)
      followers.set(anchor, group)
    }
  }

  return [
    ...leading,
    ...kept.flatMap((id) => [id, ...(followers.get(id) ?? [])]),
  ]
}

// =============== HOOKS

/**
 * The width one column renders at: the user's override, else the column
 * document's width, or falling back to the default.
 */
export function useColumnWidth(column: Pick<Column, 'id' | 'width'>): number {
  const rundownId = rundownPageStore.rundown?.id
  assert(rundownId, 'useColumnWidth: no rundown is loaded')

  const layouts = useSnapshot(columnLayoutStore)
  const override = layouts[rundownId]?.columnWidths[column.id]

  return override || column.width || COLUMN_DEFAULT_WIDTH
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(columnLayoutStore, { name: 'columnLayoutStore', enabled: true })
}
// #endregion
